import { useState, useEffect, useRef } from 'react';
import { Check, X, Loader } from 'lucide-react';

interface CodeEditProgressProps {
  summary: string;
  completeSummary: string;
  estimatedLines: number;
  currentLines: number;
  isComplete: boolean;
  isApplied?: boolean; // Whether code has been applied to editor
  isError?: boolean; // Whether validation failed
  isAborted?: boolean; // Whether aborted by user
}

export default function CodeEditProgress({
  summary,
  completeSummary,
  estimatedLines,
  currentLines,
  isComplete,
  isApplied = false,
  isError = false,
  isAborted = false
}: CodeEditProgressProps) {
  // Calculate progress percentage
  const progress = estimatedLines > 0 
    ? Math.min(Math.round((currentLines / estimatedLines) * 100), 99)
    : 0;
  
  // Use 100% if complete
  const targetProgress = isComplete ? 100 : progress;
  
  // Smoothly animated progress with lerp at 10fps (only animates when needed)
  const [displayProgress, setDisplayProgress] = useState(targetProgress);
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Lerp animation at 10fps - only runs when target changes
  useEffect(() => {
    // If target hasn't changed, no animation needed
    if (Math.abs(displayProgress - targetProgress) < 0.1) {
      return;
    }
    
    // Cancel any existing animation
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
    }
    
    const animate = () => {
      setDisplayProgress(current => {
        const diff = targetProgress - current;
        
        // Snap to target if very close (within 0.5%)
        if (Math.abs(diff) < 0.5) {
          animationTimerRef.current = null;
          return targetProgress;
        }
        
        // Lerp: smoothly interpolate towards target
        const lerpFactor = 0.6; // Slightly faster since we're at lower fps
        const newValue = current + diff * lerpFactor;
        
        // Continue animation if not at target yet (10fps = 100ms interval)
        animationTimerRef.current = setTimeout(animate, 300);
        
        return newValue;
      });
    };
    
    // Start animation
    animationTimerRef.current = setTimeout(animate, 300);
    
    // Cleanup on unmount or when target changes
    return () => {
      if (animationTimerRef.current) {
        clearTimeout(animationTimerRef.current);
      }
    };
  }, [targetProgress, displayProgress]); // Run when target changes

  // Completed state - show loader if not yet applied, check if applied, error icon, or aborted icon
  if (isComplete || isAborted) {
    return (
      <div className={`border rounded-lg px-3 py-2 ${
        isError 
          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' 
          : isAborted
          ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
          : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
      }`}>
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <p className={`text-sm ${
              isError 
                ? 'text-red-700 dark:text-red-300' 
                : isAborted
                ? 'text-yellow-700 dark:text-yellow-300'
                : 'text-gray-700 dark:text-dark-text'
            }`}>
              {completeSummary}
            </p>
          </div>
          <div className="flex-shrink-0">
            {isError ? (
              <X className="w-4 h-4 text-red-600 dark:text-red-500" />
            ) : isAborted ? (
              <X className="w-4 h-4 text-yellow-600 dark:text-yellow-500" />
            ) : isApplied ? (
              <Check className="w-4 h-4 text-green-600 dark:text-green-500" />
            ) : (
              <Loader className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />
            )}
          </div>
        </div>
      </div>
    );
  }

  // In-progress state - animated gradients
  return (
    <div className="relative bg-gray-200 dark:bg-gray-950 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 shadow-xl overflow-hidden hover:bg-gray-300 dark:hover:bg-gray-900 transition-colors">
      {/* Animated gradient backgrounds - same as ProcessingPanel */}
      
      {/* Cyan gradient - fast */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(73, 243, 255, 0.51) 0%, transparent 60%)',
          backgroundSize: '200% 200%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 12s linear infinite'
        }}
      />
      
      {/* Purple gradient - slow */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(154, 47, 255, 0.53) 0%, transparent 70%)',
          backgroundSize: '200% 200%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 19s ease-in-out infinite'
        }}
      />
      
      {/* Green gradient - medium */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 241, 45, 0.69) 0%, transparent 50%)',
          backgroundSize: '150% 150%',
          mixBlendMode: 'overlay',
          animation: 'slideGradient 8s linear infinite'
        }}
      />
      
      {/* Pink gradient - very slow */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(236, 72, 154, 0.49) 0%, transparent 80%)',
          backgroundSize: '250% 250%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 12s linear infinite'
        }}
      />
      
      {/* Blue gradient - reverse direction */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.09) 0%, transparent 65%)',
          backgroundSize: '180% 180%',
          mixBlendMode: 'plus-lighter',
          animation: 'slideGradient 7.5s linear infinite reverse'
        }}
      />
      
      {/* Solid color background layer - sits above gradients */}
      <div 
        className="absolute inset-1 bg-white/90 dark:bg-gray-900/80 z-[5] rounded-md"
      />
      
      {/* Content */}
      <div className="relative z-10 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-700 dark:text-dark-text opacity-80">
            {`${summary}...`}
          </p>
        </div>
        <div className="flex-shrink-0">
          <span className="text-xs text-gray-500 dark:text-dark-text-muted font-mono opacity-80">
            {Math.round(displayProgress)}%
          </span>
        </div>
      </div>
    </div>
  );
}

